import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { Seo } from "@/components/site/Seo";

export default function Memberships() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/memberships/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const join = async (lookup_key) => {
    if (!user) {
      toast.message("Create a free account to join a membership.");
      navigate(`/join?next=memberships`);
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  return (
    <div>
      <Seo title="Memberships | Psalm 30:2 Chiropractic Gardner KS" description="Wellness membership plans at Psalm 30:2 Chiropractic in Gardner, Kansas — save every month on ongoing care." />
      <PageHero eyebrow="Memberships" title={<>Care that fits<br />your rhythm.</>} intro="Wellness memberships for those ready to move from a care plan into ongoing care. Save every month, plus 20% off supplements and elderberry — and your price is locked in for life." />
      <section className="bg-ivory pb-28 px-6 md:px-10 lg:px-14" data-testid="memberships-grid">
        <div className="max-w-[1500px] mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((m, i) => (
            <Reveal key={m.lookup_key} delay={(i % 3) * 70}>
              <div className={`h-full flex flex-col rounded-sm border p-8 ${m.featured ? "bg-espresso text-ivory border-espresso" : "bg-linen border-espresso/10"}`} data-testid={`membership-card-${m.lookup_key}`}>
                <div className={`overline mb-4 ${m.featured ? "!text-brass" : ""}`}>{m.who}</div>
                <h3 className="font-serif text-3xl mb-2 leading-tight">{m.name}</h3>
                <div className="mb-6">
                  <span className="font-serif text-4xl">{m.price}</span>
                  <span className={`text-sm ml-1 ${m.featured ? "text-ivory/60" : "text-taupe"}`}>{m.cadence}</span>
                </div>
                <ul className="space-y-3 flex-1">
                  {(m.includes || []).map((inc) => (
                    <li key={inc} className="flex gap-3 text-sm font-light">
                      <Check size={16} className={m.featured ? "text-brass shrink-0 mt-0.5" : "text-sage shrink-0 mt-0.5"} />
                      <span className={m.featured ? "text-ivory/85" : "text-espressosoft/80"}>{inc}</span>
                    </li>
                  ))}
                </ul>
                <p className={`text-xs mt-6 ${m.featured ? "text-ivory/50" : "text-taupe"}`}>{m.note}</p>
                <button
                  data-testid={`membership-join-${m.lookup_key}`}
                  onClick={() => join(m.lookup_key)}
                  disabled={busy === m.lookup_key}
                  className={`mt-6 inline-flex items-center justify-center gap-2 w-full rounded-full py-3.5 text-xs uppercase tracking-[0.18em] transition-all disabled:opacity-60 ${
                    m.featured ? "bg-brass text-ivory hover:bg-[#c8912f]" : "bg-espresso text-ivory hover:bg-brass"
                  }`}
                >
                  {busy === m.lookup_key ? <Loader2 size={15} className="animate-spin" /> : null}
                  {busy === m.lookup_key ? "Starting…" : "Join This Plan"}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="max-w-[1500px] mx-auto mt-10 text-xs text-taupe">Secure recurring payment via Stripe. New memberships include a 2-month minimum commitment; after that you can cancel anytime from your portal — and your price stays locked in for as long as you keep your membership. Questions? Call (913) 213-1288 or ask Dr. Brandon at your next visit.</p>
      </section>
    </div>
  );
}
